import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  FormLabel,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  TextField,
  InputAdornment,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { format, parse } from 'date-fns';
import { DEFAULT_WORK_DURATION_STEP_MINUTES, formatDuration } from '@/utils/duration';
import { DurationHoursInput } from './DurationHoursInput';
import { AvailabilityTimePicker } from './AvailabilityTimePicker';
import {
  DEFAULT_PICKUP_BUFFER_MINUTES,
  hasKnownDuration,
  pickupFitsWorkWindow,
} from './pickupEstimate';
import type { SelectedServiceData } from './ServiceStep';
import type { SchedulingType } from '@/types';
import { useExactTimeOutsideShiftWarning } from '@/features/calendar/outsideShift';

interface SchedulingStepProps {
  scheduledAtStr: string;
  setScheduledAtStr: (v: string) => void;
  vehicleDropoffAtStr?: string;
  setVehicleDropoffAtStr?: (v: string) => void;
  pickupAtStr: string;
  setPickupAtStr: (v: string) => void;
  estimatedDurationHours?: number;
  /**
   * Calendar-reserved duration setter (#2336). When omitted the field is
   * still shown (including 0) but read-only. Create always passes this so
   * the overall duration stays editable even when services exist.
   */
  setEstimatedDurationHours?: (v: number) => void;
  /**
   * Replaces the overall duration field. Quote-accept with services uses
   * this for per-service planned hours and still passes the summed hours
   * as `estimatedDurationHours` for availability (#2667).
   */
  durationOverride?: ReactNode;
  /** Workshop step for the duration slider and arrows; 30 when unset (#3091). */
  durationStepMinutes?: number;
  mechanicIds?: string[];
  selectedServices: SelectedServiceData[];
  customerWaits: boolean;
  setCustomerWaits: (v: boolean) => void;
  allowMinorMaintenance: boolean;
  setAllowMinorMaintenance: (v: boolean) => void;
  preapprovedLimitInput: string;
  setPreapprovedLimitInput: (v: string) => void;
  jointInspectionRequested: boolean;
  setJointInspectionRequested: (v: boolean) => void;
  setIsDirty: (v: boolean) => void;
  /**
   * Exact-time vs during-the-day selection (#1138). Required: there is no
   * defensible hardcoded default any more — the initial value is the
   * workshop's `defaultSchedulingType` setting, which only the caller can
   * read (#1931). A caller that omits `setSchedulingType` still gets the
   * behavior for the type it passes, just without the toggle.
   */
  schedulingType: SchedulingType;
  setSchedulingType?: (v: SchedulingType) => void;
  /**
   * True once the user has picked a type themselves. "Customer waits" then
   * does not override it (#2115) — it is guidance, not a lock.
   */
  schedulingTypeTouched?: boolean;
  /** Workshop default restored when "Customer waits" is cleared (#2115). */
  defaultSchedulingType?: SchedulingType;
  /** Marks the type as a manual choice so customer-waits will not override it. */
  onSchedulingTypeUserChange?: () => void;
  /** Workshop pickup buffer in minutes; 60 when the workshop has never set it (#2117). */
  pickupBufferMinutes?: number;
  /**
   * Hide the in-step outside-shift alert when the parent shows the same
   * warning next to confirm (#2796).
   */
  hideOutsideShiftWarning?: boolean;
}

export function SchedulingStep({
  scheduledAtStr,
  setScheduledAtStr,
  vehicleDropoffAtStr = '',
  setVehicleDropoffAtStr,
  pickupAtStr,
  setPickupAtStr,
  estimatedDurationHours,
  setEstimatedDurationHours,
  durationOverride,
  durationStepMinutes = DEFAULT_WORK_DURATION_STEP_MINUTES,
  mechanicIds,
  selectedServices,
  customerWaits,
  setCustomerWaits,
  allowMinorMaintenance,
  setAllowMinorMaintenance,
  preapprovedLimitInput,
  setPreapprovedLimitInput,
  jointInspectionRequested,
  setJointInspectionRequested,
  setIsDirty,
  schedulingType,
  setSchedulingType,
  schedulingTypeTouched = false,
  defaultSchedulingType = 'floating',
  onSchedulingTypeUserChange,
  pickupBufferMinutes = DEFAULT_PICKUP_BUFFER_MINUTES,
  hideOutsideShiftWarning = false,
}: SchedulingStepProps) {
  const { t } = useTranslation('workorders');

  const durationHours = estimatedDurationHours ?? 0;
  const durationKnown = hasKnownDuration(durationHours);

  // Selected date from the picker (YYYY-MM-DD portion)
  const selectedDate = scheduledAtStr ? scheduledAtStr.split('T')[0] ?? '' : '';

  // Unique service IDs of the selected services — the availability endpoints
  // use them for skill-aware qualified capacity (epic #1140). With no skill
  // requirements every active mechanic qualifies, so passing them is always
  // safe.
  const serviceIds = useMemo(
    () => Array.from(new Set(selectedServices.map((s) => s.serviceId).filter(Boolean))),
    [selectedServices],
  );

  // Switching type keeps the chosen date: floating drops the time to 00:00,
  // exact falls back to a morning default the slot grid then refines (#1142).
  const applySchedulingType = (next: SchedulingType) => {
    if (!setSchedulingType || next === schedulingType) return;
    setSchedulingType(next);
    if (selectedDate) {
      setScheduledAtStr(next === 'floating' ? `${selectedDate}T00:00` : `${selectedDate}T08:00`);
    }
    setIsDirty(true);
  };

  const handleSchedulingTypeChange = (next: SchedulingType) => {
    applySchedulingType(next);
    onSchedulingTypeUserChange?.();
  };

  const handleCustomerWaitsChange = (checked: boolean) => {
    setCustomerWaits(checked);
    setIsDirty(true);
    if (!setSchedulingType || schedulingTypeTouched) return;
    applySchedulingType(checked ? 'exact' : defaultSchedulingType);
  };

  const pickupTooEarly =
    schedulingType === 'exact' &&
    !pickupFitsWorkWindow({
      arrivalAtStr: scheduledAtStr,
      pickupAtStr,
      durationHours,
      bufferMinutes: pickupBufferMinutes,
    });

  const outsideShift = useExactTimeOutsideShiftWarning({
    schedulingType,
    scheduledAtStr,
    durationHours,
    ...(mechanicIds !== undefined ? { mechanicIds } : {}),
  });

  let pickupHelperText = t('create.requestedPickupAtHint');
  if (schedulingType === 'exact' && durationKnown) {
    pickupHelperText = t('create.pickupEstimateHint', {
      duration: formatDuration(durationHours),
      buffer: pickupBufferMinutes,
    });
  } else if (schedulingType === 'exact') {
    pickupHelperText = t('create.pickupEstimateUnknownHint');
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Customer waits first: it is the question that decides whether an
          exact time is needed. Guidance, not a lock (#2115). */}
      <FormControlLabel
        control={
          <Checkbox
            checked={customerWaits}
            onChange={(e) => {
              handleCustomerWaitsChange(e.target.checked);
            }}
          />
        }
        label={t('create.customerWaits')}
      />

      {/* Calendar-reserved duration: always visible, including 0 (#2336).
          Quote-accept with services replaces this with per-service editors
          and still displays the total there (#2667). */}
      {durationOverride ?? (
        <DurationHoursInput
          value={durationHours}
          {...(setEstimatedDurationHours
            ? {
                onChange: (hours: number) => {
                  setEstimatedDurationHours(hours);
                  setIsDirty(true);
                },
              }
            : {})}
          stepMinutes={durationStepMinutes}
          label={t('create.estimatedDuration')}
          helperText={
            durationKnown ? t('create.estimatedDurationHint') : t('create.estimatedDurationZeroHint')
          }
          warning={!durationKnown}
        />
      )}

      {/* Exact time vs during-the-day (#1138). Only rendered for callers that opt in. */}
      {setSchedulingType && (
        <ToggleButtonGroup
          value={schedulingType}
          exclusive
          onChange={(_e, value: SchedulingType | null) => {
            if (value) handleSchedulingTypeChange(value);
          }}
          size="small"
          aria-label={t('create.schedulingTypeLabel')}
        >
          {/* "During the day" first — it is what most work is (#1931). */}
          <ToggleButton value="floating">{t('create.schedulingTypeFloating')}</ToggleButton>
          <ToggleButton value="exact">{t('create.schedulingTypeExact')}</ToggleButton>
        </ToggleButtonGroup>
      )}

      {/* Work appointment / queue day and vehicle drop-off are independent. */}
      <Box>
        <FormLabel sx={{ display: 'block', mb: 1 }}>
          {t(schedulingType === 'floating' ? 'create.scheduledDay' : 'create.scheduledTime')}
        </FormLabel>
        <AvailabilityTimePicker
          value={scheduledAtStr}
          onChange={(v) => {
            setScheduledAtStr(v);
            setIsDirty(true);
          }}
          mode={schedulingType}
          estimatedDurationHours={durationHours}
          serviceIds={serviceIds}
          {...(mechanicIds !== undefined ? { mechanicIds } : {})}
        />
      </Box>

      {setVehicleDropoffAtStr && (
        <TextField
          type="datetime-local"
          size="small"
          label={t('create.vehicleDropoffAt')}
          helperText={t('create.vehicleDropoffAtHint')}
          value={vehicleDropoffAtStr}
          onChange={(event) => {
            setVehicleDropoffAtStr(event.target.value);
            setIsDirty(true);
          }}
          slotProps={{ inputLabel: { shrink: true }, htmlInput: { step: 900 } }}
        />
      )}

      {/* Date/time picker for requested pickup */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        <DateTimePicker
          label={t('create.requestedPickupAt')}
          value={pickupAtStr ? parse(pickupAtStr, "yyyy-MM-dd'T'HH:mm", new Date()) : null}
          onChange={(value) => {
            if (value) {
              setPickupAtStr(format(value, "yyyy-MM-dd'T'HH:mm"));
            }
            setIsDirty(true);
          }}
          minDateTime={scheduledAtStr ? parse(scheduledAtStr, "yyyy-MM-dd'T'HH:mm", new Date()) : undefined}
          minutesStep={15}
          ampm={false}
          slotProps={{
            textField: {
              fullWidth: true,
              // Keep the field in floating mode so a user-set pickup still
              // works. Midnight is a day marker, so duration+buffer is not a
              // real suggestion — use the generic hint instead (#2117).
              // Unknown duration must not look like a buffer-only estimate (#2335).
              helperText: pickupHelperText,
            },
            openPickerIcon: { fontSize: 'small' },
          }}
          sx={{ flex: 1 }}
        />
      </Box>

      {pickupTooEarly && (
        <Alert severity="warning">{t('create.pickupTooEarlyWarning')}</Alert>
      )}

      {!hideOutsideShiftWarning && outsideShift && (
        <Alert severity="warning" data-testid="outside-shift-warning">
          {t('create.outsideShiftWarning')}
        </Alert>
      )}

      {/* Remaining flags — own group so the date pickers above keep their space */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={jointInspectionRequested}
              onChange={(e) => {
                setJointInspectionRequested(e.target.checked);
                setIsDirty(true);
              }}
            />
          }
          label={t('create.jointInspectionRequested')}
        />
        <Tooltip title={t('create.allowMinorMaintenanceDesc')} placement="top" arrow>
          <FormControlLabel
            control={
              <Checkbox
                checked={allowMinorMaintenance}
                onChange={(e) => {
                  setAllowMinorMaintenance(e.target.checked);
                  setIsDirty(true);
                }}
              />
            }
            label={t('create.allowMinorMaintenance')}
          />
        </Tooltip>
      </Box>

      {/* Pre-approved additional work limit (#66) */}
      <TextField
        label={t('preapprovedLimit.label')}
        placeholder={t('preapprovedLimit.placeholder')}
        helperText={t('preapprovedLimit.hint')}
        value={preapprovedLimitInput}
        onChange={(e) => {
          setPreapprovedLimitInput(e.target.value);
          setIsDirty(true);
        }}
        type="number"
        slotProps={{
          htmlInput: {
            min: 0,
            step: 10,
          },
          input: {
            endAdornment: <InputAdornment position="end">€</InputAdornment>,
          },
        }}
        fullWidth
        size="small"
      />
    </Box>
  );
}
